import { defineStore } from "pinia";
import { ref, onMounted, onBeforeUnmount, type Ref } from "vue";

export interface YotpoWidgetsContainer {
    initWidget: (instanceId: string | undefined, element: Element | null) => void;
}

function getYotpoApi(): YotpoWidgetsContainer | null {
    const api = (globalThis as Record<string, unknown>)["yotpoWidgetsContainer"];
    if (api && typeof (api as YotpoWidgetsContainer).initWidget === "function") {
        return api as YotpoWidgetsContainer;
    }
    return null;
}

export const useYotpoStore = defineStore('useYotpoStore', ()=>{

    const yotpoReady = ref(false);
    const interval = ref(null);
    const POLL_INTERVAL_MS = 100;
    const MAX_ATTEMPTS = 100; // ~10s at 100ms

    function checkYotpoReady() {
        let attempts = 0;
        if(!!yotpoReady.value || !!interval.value) {
            return;
        }
        interval.value = setInterval(() => {
            attempts++
            const api = getYotpoApi();
            if (api) {
                stop();
                yotpoReady.value = true;
            } else if (attempts >= MAX_ATTEMPTS) {
                stop();
                yotpoReady.value = false;
            }
        }, POLL_INTERVAL_MS);
    }

    function stop() {
        if(!!interval.value) {
            clearInterval(interval.value);
            interval.value = null;
        }
    }

    function initYotpoWidget(instanceId: string, yotpoRef: Ref) {
        const api = getYotpoApi();
        if (!!yotpoReady.value && !!instanceId && !!yotpoRef.value && api) {
            api.initWidget(instanceId, yotpoRef.value);
        }
    }

    const activeYotpoWidgets = ref(0);
    onMounted(() => {
        activeYotpoWidgets.value++;
        if(!yotpoReady.value && !interval.value) {
            checkYotpoReady();
        }
    })

    onBeforeUnmount(() => {
        activeYotpoWidgets.value = Math.max(0, activeYotpoWidgets.value - 1);
        if(activeYotpoWidgets.value === 0) {
            stop();
        }
    })

    function resetForTests(): void {
        stop();
        yotpoReady.value = false;
        activeYotpoWidgets.value = 0;
    }

    return {
        yotpoReady,
        initYotpoWidget,
        checkYotpoReady,
        stop,
        resetForTests,
        POLL_INTERVAL_MS,
        MAX_ATTEMPTS,
    };
});